// Copy CENTRALIZADA de la experiencia de rollout hacia el MERCHANT (comercio).
//
// REGLA TRANSVERSAL (decisión de producto): lenguaje técnico CERO. Nunca decir
// "experimento", "prueba", "beta", "variante", "A/B", "control/treatment", "rollout".
// Solo "versión nueva" / "versión anterior". Este archivo es la ÚNICA fuente de verdad
// de los textos visibles: editar aquí, no en los componentes. El equipo de producto da
// el OK final sobre la copy.
//
// Estructura: un objeto por superficie. Las claves describen el contexto, los valores son
// el texto literal que ve el comercio.

// ── A. EvacuatedNotice (reason-aware) ──────────────────────────────────────────────
// El operador regresó este comercio a la versión anterior. El motivo viaja en el contrato
// feature-version como `evac_reason`. Dos variantes de copy + un default robusto.
export const EVACUATED_NOTICE = {
  // A1 — pausa temporal (evac_reason='iterating'). También es el DEFAULT cuando el campo no
  // viene (degradación: el backend lo agrega en paralelo). Tono tranquilizador, reversible.
  iterating: {
    title: 'Volvimos a la versión anterior de tu menú por ahora',
    body: 'Estamos afinando unas mejoras y te avisamos cuando la nueva esté lista otra vez. Tu información sigue intacta.',
  },
  // B2 — descontinuado (evac_reason='discontinued'). Cierre amable, sin promesa de regreso.
  discontinued: {
    title: 'Tu menú vuelve a la versión anterior',
    body: 'Gracias por acompañarnos probando la nueva.',
  },
} as const;

// ── B. Banner de anuncio GA (C2) ───────────────────────────────────────────────────
// El comercio ya está graduado (ga_forced): la versión nueva es la oficial. Banner tipo
// "nueva funcionalidad", descartable y no intrusivo.
export const GA_ANNOUNCEMENT = {
  title: 'Estrenamos una nueva versión de Mi Menú',
  body: 'Más cómoda desde el celular y mejor organizada en tablet y computadora. Ya está activa en tu cuenta.',
  dismiss: 'Entendido',
  dismissAria: 'Descartar aviso',
} as const;

// ── C. Prompt de feedback al revertir (D1) ─────────────────────────────────────────
// Al volver a la versión anterior, preguntamos (omitible) por qué. El motivo viaja en la
// telemetría del revert (fire-and-forget). NUNCA bloquea el revert.
export const REVERT_FEEDBACK = {
  title: '¿Nos cuentas por qué volviste a la versión anterior?',
  subtitle: 'Tu respuesta nos ayuda a mejorar. Es opcional.',
  // Los `value` son estables (van en el payload de telemetría, no se traducen); los `label`
  // son la copy visible. Mantener el lenguaje no técnico.
  options: [
    { value: 'hard_to_use', label: 'Me costó usarla' },
    { value: 'missing_feature', label: 'Me faltaba algo' },
    { value: 'prefer_previous', label: 'Prefiero la anterior' },
    { value: 'other', label: 'Otro' },
  ],
  textPlaceholder: 'Cuéntanos un poco más (opcional)',
  submit: 'Enviar',
  skip: 'Omitir',
  closeAria: 'Cerrar',
  thanks: '¡Gracias por tu opinión!',
} as const;

export type RevertFeedbackReason = (typeof REVERT_FEEDBACK.options)[number]['value'];

// ── D. CTA por ROL (Item E #1) ─────────────────────────────────────────────────────────
// El root decide el default per-comercio (switch durable); el operador no-root solo "echa un
// vistazo" (preview per-usuario, no toca el comercio). La copy del CTA cambia por rol EN RENDER.
export const ROLE_CTA = {
  // ROOT: cambia la versión activa para TODO el comercio (acción durable, reversible).
  rootTry: 'Cambiar a la versión nueva',
  // NO-ROOT: vista previa personal, sin cambiar nada para el comercio.
  operatorPreview: 'Echa un vistazo',
} as const;

// ── E. Barra de vista previa per-usuario (Item E #2) ───────────────────────────────────
// Mientras el operador mira la versión nueva en preview, una barra le recuerda que es solo para
// él y que el comercio sigue en la anterior. "Volver a la actual" apaga el preview (no revierte
// nada del comercio).
export const PREVIEW_BAR = {
  label: 'Estás viendo una vista previa · para tu comercio sigue la anterior',
  back: 'Volver a la actual',
  backAria: 'Salir de la vista previa y volver a la versión actual',
} as const;

// ── F. Prompt de feedback por USO (Item E #3) ──────────────────────────────────────────
// Tras N acciones reales en la versión nueva (vista previa o adoptada), preguntamos —OMITIBLE—
// qué le pareció. El encuadre es POSITIVO ("qué te gusta"), neutro mexicano con "tú", sin voseo.
// El motivo viaja por telemetría (behavior usage_feedback) con by_user_id. Omitible y con opt-out
// durable ("No volver a preguntar").
export const USAGE_FEEDBACK = {
  title: '¿Qué te está gustando de la versión nueva?',
  subtitle: 'La estuviste usando un rato. Cuéntanos qué te funciona mejor. Es opcional.',
  optOut: 'No volver a preguntar',
} as const;

// ── G. Encuesta de PERMANENCIA (adopción / "quedarme") ─────────────────────────────────
// Se dispara cuando el comercio ADOPTA la versión nueva: ya sea pasivamente (el backend resolvió
// menu_state='adopted', p.ej. por uso sostenido) o explícitamente (el root pulsó "Quedarme con la
// versión nueva"). Reusa el componente de feedback con ENCUADRE POSITIVO: motivos de lo que SÍ les
// gustó + un abierto "¿qué le falta?". El motivo viaja por telemetría (behavior 'usage_feedback')
// con trigger_source ∈ {adopted, kept}. Lenguaje no técnico, neutro mexicano con "tú", SIN voseo.
// Los `value` son estables (van en el payload, no se traducen); los `label` son la copy visible.
export const PERMANENCE_SURVEY = {
  title: '¡Qué bueno que te quedas con la versión nueva!',
  subtitle: '¿Qué fue lo que más te gustó? Tu respuesta nos ayuda a seguir mejorándola. Es opcional.',
  options: [
    { value: 'faster_to_manage', label: 'Más rápida de gestionar' },
    { value: 'clearer_organized', label: 'Más clara y organizada' },
    { value: 'better_on_mobile', label: 'Mejor en el celular' },
    { value: 'easier_to_find', label: 'Encuentro todo más fácil' },
    { value: 'other', label: 'Otro' },
  ],
  textPlaceholder: '¿Qué le falta o qué mejorarías? (opcional)',
  optOut: 'No volver a preguntar',
} as const;

export type PermanenceSurveyReason = (typeof PERMANENCE_SURVEY.options)[number]['value'];

// ── H. CTA "Quedarme con la versión nueva" (adopción explícita) ────────────────────────
// En treatment, el root puede CONFIRMAR que se queda con la versión nueva. Cuenta como adopción
// (el backend pone state='adopted' con trigger 'kept') y dispara la encuesta de permanencia.
export const KEEP_NEW = {
  cta: 'Quedarme con la versión nueva',
} as const;

// ── I. Descubrimiento de impresión Bluetooth (feature impresion-ble) ────────────────────
// Coachmark de invitación anclado al LUGAR exacto donde vive la funcionalidad (Ajustes →
// Impresión). Solo se muestra en dispositivos COMPATIBLES (Web Bluetooth disponible): no se
// invita donde no puede usarse. Misma regla transversal: cero jerga técnica de experimentos.
export const BLE_PRINTING_DISCOVERY = {
  badge: 'NUEVO',
  navTag: 'Nuevo',
  title: 'Imprime tus tickets por Bluetooth',
  body: 'Conecta tu impresora térmica directo desde el panel y los pedidos se imprimen solos, sin instalar nada.',
  cta: 'Probar ahora',
  dismiss: 'Ahora no',
  // Modo informativo para operadores no-root: activarla muta la variante de TODO el comercio
  // (decisión del dueño). El coachmark se muestra sin CTA de switch.
  askOwner: 'Pídele al dueño de la cuenta que la active desde su panel.',
  dismissInfo: 'Entendido',
} as const;

// ── J. Gestión de versión de impresión Bluetooth (feature impresion-ble, treatment) ─────
// "Quitar" = el comercio sale de la feature (revert del rollout) con RESET TOTAL del estado
// BLE de los equipos (decisión de producto 2026-06-10: cero estado fantasma; al regresar se
// configura de nuevo). "Quedarme" = adopción explícita (kept). Solo root efectivo.
export const BLE_VERSION_CONTROLS = {
  keepCta: 'Quedarme con la impresión Bluetooth',
  removeCta: 'Quitar la impresión Bluetooth de mi panel',
} as const;

export const BLE_REMOVE_CONFIRM = {
  title: '¿Quitar la impresión Bluetooth?',
  body: 'Tus equipos dejarán de imprimir por Bluetooth y volverá la impresión tradicional. Si regresas, configurarás tus impresoras de nuevo.',
  confirm: 'Sí, quitarla',
  cancel: 'Cancelar',
} as const;

// Motivos del feedback al quitar la impresión BT. Mismos REQUISITOS que los de menú: los
// `value` son slugs ESTABLES (jamás se renombran una vez emitidos; el ERP agrupa por ellos)
// y validados por backend (^[a-z][a-z0-9_]{1,39}$). CS seedea ESTE MISMO set como catálogo
// del experimento en el ERP (espejo); si el catálogo no viene, estos son el fallback.
export const BLE_REVERT_FEEDBACK = {
  title: '¿Qué te hizo quitarla?',
  subtitle: 'Tu respuesta nos ayuda a mejorarla. Es opcional.',
  options: [
    { value: 'could_not_connect', label: 'No logré conectar mi impresora' },
    { value: 'print_failed', label: 'Fallaban las impresiones' },
    { value: 'prefer_current_method', label: 'Prefiero mi método actual' },
    { value: 'other', label: 'Otro' },
  ],
  textPlaceholder: 'Cuéntanos más (opcional)',
} as const;

export const BLE_PERMANENCE_SURVEY = {
  title: '¡Gracias por quedarte con la impresión Bluetooth!',
  subtitle: '¿Qué fue lo que más te sirvió? Tu respuesta nos ayuda a seguir mejorándola. Es opcional.',
  options: [
    { value: 'faster_printing', label: 'Imprime más rápido' },
    { value: 'auto_print_orders', label: 'Los pedidos se imprimen solos' },
    { value: 'no_extra_software', label: 'Sin instalar nada extra' },
    { value: 'other', label: 'Otro' },
  ],
  textPlaceholder: '¿Qué le falta o qué mejorarías? (opcional)',
  optOut: 'No volver a preguntar',
} as const;
